import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { dollars } from "../lossFrameMoney";
import { showsPerformanceFeedback } from "../../../shared/experimentBehavior.js";

const DEFAULT_TASK_DURATION_SECONDS = 120;
const DEFAULT_COUNTDOWN_SECONDS = 0;
const ARENA_WIDTH = 720;
const ARENA_HEIGHT = 360;
const BASE_RADIUS = 14.4;
const SMIRKING_FACE = "\u{1F60F}";
const TIRED_FACE = "\u{1F62B}";

export const TRACKING_JITTER_STRENGTH = 0.13;
export const TRACKING_DAMPING = 0.97;
export const TRACKING_SPEED_SCALE = 4;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function finiteNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function trackingResultForInsideSeconds(radiusPx, durationSeconds, insideSeconds) {
  const totalMs = durationSeconds * 1000;
  const insideMs = clamp(finiteNumber(insideSeconds) * 1000, 0, totalMs);
  return {
    insideMs,
    totalMs,
    durationSeconds,
    radiusPx,
  };
}

export function TrackingTaskStage({
  heading,
  description,
  resultKey,
  radiusMultiplierKey,
  durationSeconds: durationSecondsProp,
  countdownSeconds: countdownSecondsProp,
  dollarsPerSecond = 0,
  startingMoneyDollars = null,
  emojiMode = false,
  lossFrame = false,
}) {
  const TASK_DURATION_SECONDS = durationSecondsProp ?? DEFAULT_TASK_DURATION_SECONDS;
  const TASK_DURATION_MS = TASK_DURATION_SECONDS * 1000;
  const countdownSecondsNumber = Number(countdownSecondsProp ?? DEFAULT_COUNTDOWN_SECONDS);
  const COUNTDOWN_MS = Math.max(
    0,
    Number.isFinite(countdownSecondsNumber) ? countdownSecondsNumber * 1000 : 0
  );
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const design = game.get("design") || {};
  const testMode = design.testMode === true;
  const showPerformanceFeedback = showsPerformanceFeedback(design);

  const radiusMultiplier = Number(player.get(radiusMultiplierKey) ?? 1);
  const radius = BASE_RADIUS * (Number.isFinite(radiusMultiplier) ? radiusMultiplier : 1);

  const [result, setResult] = useState(() => player.stage.get(resultKey) || null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [liveInsideMs, setLiveInsideMs] = useState(0);
  const [countdownRemainingMs, setCountdownRemainingMs] = useState(COUNTDOWN_MS);
  const [isCursorInside, setIsCursorInside] = useState(false);
  const [circlePosition, setCirclePosition] = useState({
    x: ARENA_WIDTH / 2,
    y: ARENA_HEIGHT / 2,
  });
  const [manualInsideSeconds, setManualInsideSeconds] = useState(() =>
    TASK_DURATION_SECONDS.toString()
  );

  const elapsedMsRef = useRef(0);
  const insideMsRef = useRef(0);
  const countdownRemainingMsRef = useRef(COUNTDOWN_MS);
  const mousePositionRef = useRef({ x: null, y: null });
  const velocityRef = useRef({ x: 0, y: 0 });
  const circlePositionRef = useRef({
    x: ARENA_WIDTH / 2,
    y: ARENA_HEIGHT / 2,
  });
  const lastTsRef = useRef(null);
  const lastPersistMsRef = useRef(0);
  const isRunningRef = useRef(false);
  const frameRef = useRef(null);
  const hasMarkedInteractionRef = useRef(player.stage.get("interacted") === true);

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );

  useEffect(() => {
    if (player.stage.get("submit")) {
      return undefined;
    }

    const submittedResult = player.stage.get(resultKey);
    if (submittedResult) {
      setResult(submittedResult);
      setElapsedMs(Number(submittedResult.totalMs ?? 0));
      setLiveInsideMs(Number(submittedResult.insideMs ?? 0));
      return undefined;
    }

    isRunningRef.current = true;
    countdownRemainingMsRef.current = COUNTDOWN_MS;
    setCountdownRemainingMs(COUNTDOWN_MS);

    const tick = (timestamp) => {
      if (!isRunningRef.current || player.stage.get("submit")) {
        return;
      }

      if (lastTsRef.current === null) {
        lastTsRef.current = timestamp;
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      const dt = timestamp - lastTsRef.current;
      lastTsRef.current = timestamp;

      if (countdownRemainingMsRef.current > 0) {
        const nextCountdownRemainingMs = Math.max(
          0,
          countdownRemainingMsRef.current - dt
        );
        countdownRemainingMsRef.current = nextCountdownRemainingMs;
        setCountdownRemainingMs(nextCountdownRemainingMs);
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      const previousElapsed = elapsedMsRef.current;
      const nextElapsed = Math.min(TASK_DURATION_MS, previousElapsed + dt);
      const effectiveDt = Math.max(0, nextElapsed - previousElapsed);
      elapsedMsRef.current = nextElapsed;

      let vx = velocityRef.current.x * TRACKING_DAMPING + (Math.random() * 2 - 1) * TRACKING_JITTER_STRENGTH;
      let vy = velocityRef.current.y * TRACKING_DAMPING + (Math.random() * 2 - 1) * TRACKING_JITTER_STRENGTH;

      const previous = circlePositionRef.current;
      let x = previous.x + vx * (dt / 16.67) * TRACKING_SPEED_SCALE;
      let y = previous.y + vy * (dt / 16.67) * TRACKING_SPEED_SCALE;

      const minX = radius;
      const maxX = ARENA_WIDTH - radius;
      const minY = radius;
      const maxY = ARENA_HEIGHT - radius;

      if (x < minX || x > maxX) {
        vx *= -1;
        x = clamp(x, minX, maxX);
      }
      if (y < minY || y > maxY) {
        vy *= -1;
        y = clamp(y, minY, maxY);
      }

      velocityRef.current = { x: vx, y: vy };

      const mouse = mousePositionRef.current;
      let insideThisFrame = false;
      if (mouse.x !== null && mouse.y !== null) {
        const dx = mouse.x - x;
        const dy = mouse.y - y;
        if (dx * dx + dy * dy <= radius * radius) {
          insideThisFrame = true;
          insideMsRef.current += effectiveDt;
        }
      }

      const nextCirclePosition = { x, y };
      circlePositionRef.current = nextCirclePosition;
      setCirclePosition(nextCirclePosition);

      setElapsedMs(elapsedMsRef.current);
      setLiveInsideMs(insideMsRef.current);
      setIsCursorInside(insideThisFrame);

      if (elapsedMsRef.current >= TASK_DURATION_MS) {
        handleSubmitWithResult({
          insideMs: insideMsRef.current,
          totalMs: elapsedMsRef.current,
          durationSeconds: TASK_DURATION_SECONDS,
          radiusPx: radius,
        });
        return;
      }

      if (elapsedMsRef.current - lastPersistMsRef.current >= 1000) {
        player.stage.set(resultKey, {
          insideMs: insideMsRef.current,
          totalMs: elapsedMsRef.current,
          durationSeconds: TASK_DURATION_SECONDS,
          radiusPx: radius,
        });
        lastPersistMsRef.current = elapsedMsRef.current;
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      isRunningRef.current = false;
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [
    COUNTDOWN_MS,
    TASK_DURATION_MS,
    TASK_DURATION_SECONDS,
    player,
    radius,
    resultKey,
  ]);

  function handleArenaMouseMove(event) {
    if (!hasMarkedInteractionRef.current) {
      player.stage.set("interacted", true);
      hasMarkedInteractionRef.current = true;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    mousePositionRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  function handleArenaMouseLeave() {
    mousePositionRef.current = { x: null, y: null };
    setIsCursorInside(false);
  }

  function handleSubmitWithResult(nextResult) {
    if (!nextResult || player.stage.get("submit")) {
      return;
    }

    setResult(nextResult);
    setElapsedMs(TASK_DURATION_MS);
    elapsedMsRef.current = TASK_DURATION_MS;
    setLiveInsideMs(Number(nextResult.insideMs ?? 0));
    setIsCursorInside(false);
    // One mutation keeps stage completion from overtaking the final score.
    player.stage.set([
      { key: resultKey, value: nextResult },
      { key: "submit", value: true },
    ]);
    isRunningRef.current = false;
  }

  function handleSubmitWithManualScore(event) {
    event.preventDefault();

    if (!testMode || player.stage.get("submit")) {
      return;
    }

    handleSubmitWithResult(
      trackingResultForInsideSeconds(
        radius,
        TASK_DURATION_SECONDS,
        manualInsideSeconds
      )
    );
  }

  const displayedElapsedMs = result
    ? Math.max(0, finiteNumber(result.totalMs))
    : Math.max(0, elapsedMs);
  const displayedInsideMs = result
    ? Math.max(0, finiteNumber(result.insideMs))
    : Math.max(0, liveInsideMs);
  const displayedInsideSeconds = displayedInsideMs / 1000;
  const dollarsPerSecondNumber = Math.max(0, finiteNumber(dollarsPerSecond));
  const startingMoney = Math.max(
    0,
    finiteNumber(
      startingMoneyDollars,
      TASK_DURATION_SECONDS * dollarsPerSecondNumber
    )
  );
  const displayedDollars = displayedInsideSeconds * dollarsPerSecondNumber;
  const displayedLostDollars =
    Math.max(0, displayedElapsedMs - displayedInsideMs) / 1000 * dollarsPerSecondNumber;
  const displayedMoneyLeft = clamp(
    startingMoney - displayedLostDollars,
    0,
    startingMoney
  );
  const isEasyRadius = radiusMultiplier > 1;
  const isCountdownActive = !result && countdownRemainingMs > 0;
  const countdownDisplaySeconds = Math.ceil(countdownRemainingMs / 1000);
  const isMoneyBeingLost = lossFrame && !result && !isCountdownActive && !isCursorInside;
  const moneyValueClass = isMoneyBeingLost ? "text-red-600" : "text-gray-900";

  return (
    <div className="py-8 max-w-5xl mx-auto px-8 space-y-6">
      <h2 className="text-4xl font-semibold text-gray-900">{heading}</h2>
      <div
        className="rounded-md border border-gray-200 p-4 space-y-2"
        style={{ width: ARENA_WIDTH }}
      >

        <p className="text-2xl text-gray-700">{description}</p>
        {/* Reserve this line so the arena does not move when tracking starts. */}
        <p
          className={`text-2xl ${isCursorInside ? "text-green-700" : "text-red-700"} ${result || isCountdownActive ? "invisible" : ""}`}
        >
          You are{" "}
          <strong>
            {lossFrame
              ? isCursorInside ? "keeping money" : "losing money"
              : isCursorInside ? "earning" : "not earning"}
          </strong>
        </p>
        {showPerformanceFeedback ? (
          <div className="text-2xl text-gray-900">
            Time inside circle: <b>{displayedInsideSeconds.toFixed(2)}</b>s{" "}
            {lossFrame ? (
              <span>
                (money left: <b className={moneyValueClass}>{dollars(displayedMoneyLeft)}</b>)
              </span>
            ) : (
              <span>
                (money earned: <b>{dollars(displayedDollars)}</b>)
              </span>
            )}
          </div>
        ) : null}
        {/* {testMode ? (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-sm text-gray-700 space-y-1">
            <div>
              Live cursor status: {isCursorInside ? "IN CIRCLE" : "OUTSIDE"}
            </div>
            <div>
              Inside time: {(liveInsideMs / 1000).toFixed(1)}s / {(elapsedMs / 1000).toFixed(1)}s
            </div>
            <div>
              Circle size: {isEasyRadius ? "Larger target" : "Smaller target"} (radius {radius.toFixed(1)}px)
            </div>
          </div>
        ) : null}
        {result ? (
          <div className="text-sm font-medium text-green-700">
            Your time in circle: {finalInsideSeconds.toFixed(1)}s
          </div>
        ) : null}
        {testMode && !player.stage.get("submit") ? (
          <div className="text-sm font-medium text-amber-700">
            Test mode is on: circle size is {isEasyRadius ? "larger" : "smaller"} (radius {radius.toFixed(1)}px), and you can instantly complete with a set score.
          </div>
        ) : null} */}
      </div>

      <div
        className="rounded-md border border-gray-300 bg-gray-50 relative select-none"
        style={{ width: ARENA_WIDTH, height: ARENA_HEIGHT }}
        onMouseMove={handleArenaMouseMove}
        onMouseLeave={handleArenaMouseLeave}
      >
        {isCountdownActive ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white text-gray-900">
            {emojiMode ? (
              <div className="mb-6 space-y-3">
                <p className="text-xl font-semibold text-center">
                  Keep your cursor on the face.
                </p>
                <div className="flex items-center gap-3 rounded-md border border-green-600 bg-green-50 px-4 py-2">
                  <span className="text-3xl font-bold text-green-700" aria-label="Correct">✓</span>
                  <span className="text-4xl" role="img" aria-label="Tired face">{TIRED_FACE}</span>
                  <div>
                    <p className="text-xl font-semibold text-green-800">
                      {lossFrame ? "You are keeping money" : "You are earning money"}
                    </p>
                    <p className="text-base text-gray-700">Cursor inside the face</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-md border border-red-600 bg-red-50 px-4 py-2">
                  <span className="text-3xl font-bold text-red-700" aria-label="Incorrect">✗</span>
                  <span className="text-4xl" role="img" aria-label="Smirking face">{SMIRKING_FACE}</span>
                  <div>
                    <p className="text-xl font-semibold text-red-800">
                      {lossFrame ? "You are losing money" : "You are NOT earning money"}
                    </p>
                    <p className="text-base text-gray-700">Cursor outside the face</p>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="text-sm font-semibold uppercase tracking-wide text-gray-600">
              Tracking starts in
            </div>
            <div className="text-7xl font-semibold tabular-nums">
              {countdownDisplaySeconds}
            </div>
          </div>
        ) : null}
        <div
          className={
            emojiMode
              ? "group absolute flex items-center justify-center rounded-full cursor-default select-none"
              : "absolute bg-gray-500/80 border border-gray-700 rounded-full transition-colors hover:bg-gray-400/80"
          }
          style={{
            width: radius * 2,
            height: radius * 2,
            left: circlePosition.x - radius,
            top: circlePosition.y - radius,
            fontSize: radius * 1.65,
            lineHeight: 1,
          }}
        >
          {emojiMode ? (
            <>
              <span className="pointer-events-none group-hover:hidden" aria-label="Smirking face">
                {SMIRKING_FACE}
              </span>
              <span className="pointer-events-none hidden group-hover:inline" aria-label="Tired face">
                {TIRED_FACE}
              </span>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex items-center space-x-4">
        {testMode ? (
          <form
            className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            onSubmit={handleSubmitWithManualScore}
          >
            <label className="font-medium" htmlFor={`${resultKey}-manual-inside-seconds`}>
              Time inside circle
            </label>
            <input
              id={`${resultKey}-manual-inside-seconds`}
              type="number"
              min="0"
              max={TASK_DURATION_SECONDS}
              step="0.1"
              value={manualInsideSeconds}
              disabled={player.stage.get("submit")}
              onChange={(event) => setManualInsideSeconds(event.target.value)}
              className="w-24 rounded-md border border-amber-300 bg-white px-2 py-1 text-gray-900 shadow-sm focus:border-empirica-500 focus:outline-none focus:ring-1 focus:ring-empirica-500 disabled:opacity-60"
            />
            <span className="text-amber-800">seconds, 0-{TASK_DURATION_SECONDS}</span>
            <Button type="submit" disabled={player.stage.get("submit")}>
              Complete with set score (test)
            </Button>
          </form>
        ) : null}
        {player.stage.get("submit") && !allSubmitted ? (
          <span className="text-sm text-gray-500">Waiting for the other participant…</span>
        ) : null}
      </div>
    </div>
  );
}
